import { render, RenderResult } from '@testing-library/react';
import React, { lazy, Suspense } from 'react';
import { MemoryRouter, Navigate, useRoutes } from 'react-router-dom';

import { SplashScreen } from 'src/components/loading-screen';
import {
  benefitsConfig,
  categories,
  events,
  faq,
  inConcert,
  parking,
  registerProfile,
  registerUser,
  sellerCode,
  services,
  shoppingInfo,
  store,
  termsAndPolicies,
} from 'src/components/sidebar/sidebar';
import BenefitsConfig from 'src/modules/benefits-config/benefits-config';
import Forbidden from 'src/modules/error/forbidden/forbidden';
import Promotions from 'src/modules/promotions/promotions';

import { AuthGuard, RoleBasedGuard } from '../../auth/guard';
import { DefaultLayout } from '../../layouts/index';
import { paths } from '../paths';
import { authRoutes } from './auth';
import { errorRoutes } from './error';

// PAGE_IMPORTS
const InConcert = lazy(() => import('../../modules/in-concert/in-concert/in-concert'));
const Home = lazy(() => import('../../modules/home/home/home'));
const RegisterUser = lazy(() => import('../../modules/register-user/register-user/register-user'));
const ShoppingInfo = lazy(() => import('../../modules/shopping-info/shopping-info/shopping-info'));
const Notification = lazy(() => import('../../modules/notification/notification'));

const TermsAndPolicies = lazy(() => import('../../modules/terms-and-policies/terms-and-policies/terms-and-policies'));
const Service = lazy(() => import('../../modules/service/service/service'));
const Parking = lazy(() => import('../../modules/parking/parking/parking'));
// const GastronomicTuesday = lazy(() => import('../../modules/gastronomic-tuesday/gastronomic-tuesday/gastronomic-tuesday'));
const StoresRestaurantsCategories = lazy(
  () => import('../../modules/stores-restaurants-categories/stores-restaurants-categories/stores-restaurants-categories')
);
const Faq = lazy(() => import('../../modules/faq/faq/faq'));
const EventCategory = lazy(() => import('../../modules/events/events/event'));
const Stores = lazy(() => import('../../modules/stores/stores/stores'));
const Dashboard = lazy(() => import('../../modules/dashboard/dashboard'));
const UserProfile = lazy(() => import('../../modules/user-profile/user-profile/user-profile'));
const SellerCode = lazy(() => import('../../modules/seller-code/seller-code'));
export const renderWithRouter = (ui: React.ReactElement): RenderResult => render(<MemoryRouter>{ui}</MemoryRouter>);

export default function Router() {
  return useRoutes([
    {
      path: '/',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <AuthGuard>
            <Navigate to={paths.dashboard.root} replace />
          </AuthGuard>
        </Suspense>
      ),
    },
    ...authRoutes,

    // RELATION_PATH_PAGES
    {
      path: paths.inConcert.root,
      element: (
        <DefaultLayout>
          <RoleBasedGuard permissions={inConcert}>
            <InConcert />
          </RoleBasedGuard>
        </DefaultLayout>
      ),
    },
    {
      path: paths.promotions.root,
      element: (
        <DefaultLayout>
          {/* <RoleBasedGuard permissions={promotions}> */}
          <Promotions />
          {/* </RoleBasedGuard> */}
        </DefaultLayout>
      ),
    },
    {
      path: paths.home.root,
      element: (
        <DefaultLayout>
          <Home />
        </DefaultLayout>
      ),
    },
    {
      path: paths.error.forbidden,
      element: <Forbidden />,
    },

    {
      path: paths.registerUser.root,
      element: (
        <AuthGuard>
          <RoleBasedGuard permissions={registerUser}>
            <DefaultLayout>
              <RegisterUser />
            </DefaultLayout>
          </RoleBasedGuard>
        </AuthGuard>
      ),
    },
    {
      path: paths.notification.root,
      element: (
        <AuthGuard>
          <RoleBasedGuard permissions={shoppingInfo}>
            <DefaultLayout>
              <Notification />
            </DefaultLayout>
          </RoleBasedGuard>
        </AuthGuard>
      ),
    },
    {
      path: paths.shoppingInfo.root,
      element: (
        <AuthGuard>
          <RoleBasedGuard permissions={shoppingInfo}>
            <DefaultLayout>
              <ShoppingInfo />
            </DefaultLayout>
          </RoleBasedGuard>
        </AuthGuard>
      ),
    },
    {
      path: paths.service.root,
      element: (
        <AuthGuard>
          <RoleBasedGuard permissions={services}>
            <DefaultLayout>
              <Service />
            </DefaultLayout>
          </RoleBasedGuard>
        </AuthGuard>
      ),
    },
    {
      path: paths.termsAndPolicies.root,
      element: (
        <AuthGuard>
          <RoleBasedGuard permissions={termsAndPolicies}>
            <DefaultLayout>
              <TermsAndPolicies />
            </DefaultLayout>
          </RoleBasedGuard>
        </AuthGuard>
      ),
    },
    {
      path: paths.parking.root,
      element: (
        <AuthGuard>
          <RoleBasedGuard permissions={parking}>
            <DefaultLayout>
              <Parking />
            </DefaultLayout>
          </RoleBasedGuard>
        </AuthGuard>
      ),
    },
    // {
    //   path: paths.gastronomicTuesday.root,
    //   element: (
    //     <AuthGuard>
    //       <RoleBasedGuard permissions={gastronomicTuesday}>
    //         <DefaultLayout>
    //           <GastronomicTuesday />
    //         </DefaultLayout>
    //       </RoleBasedGuard>
    //     </AuthGuard>
    //   ),
    // },
    {
      path: paths.dashboard.root,
      element: (
        <AuthGuard>
          <DefaultLayout>
            <Dashboard />
          </DefaultLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.storesRestaurantsCategories.root,
      element: (
        <AuthGuard>
          <RoleBasedGuard permissions={categories}>
            <DefaultLayout>
              <StoresRestaurantsCategories />
            </DefaultLayout>
          </RoleBasedGuard>
        </AuthGuard>
      ),
    },
    {
      path: paths.events.root,
      element: (
        <AuthGuard>
          <RoleBasedGuard permissions={events}>
            <DefaultLayout>
              <EventCategory />
            </DefaultLayout>
          </RoleBasedGuard>
        </AuthGuard>
      ),
    },
    {
      path: paths.stores.root,
      element: (
        <AuthGuard>
          <RoleBasedGuard permissions={store}>
            <DefaultLayout>
              <Stores />
            </DefaultLayout>
          </RoleBasedGuard>
        </AuthGuard>
      ),
    },
    {
      path: paths.faq.root,
      element: (
        <AuthGuard>
          <RoleBasedGuard permissions={faq}>
            <DefaultLayout>
              <Faq />
            </DefaultLayout>
          </RoleBasedGuard>
        </AuthGuard>
      ),
    },

    {
      path: paths.userProfile.root,
      element: (
        <AuthGuard>
          <RoleBasedGuard permissions={registerProfile}>
            <DefaultLayout>
              <UserProfile />
            </DefaultLayout>
          </RoleBasedGuard>
        </AuthGuard>
      ),
    },
    {
      path: paths.sellerCode.root,
      element: (
        <AuthGuard>
          <DefaultLayout>
            <RoleBasedGuard permissions={sellerCode}>
              <SellerCode />
            </RoleBasedGuard>
          </DefaultLayout>
        </AuthGuard>
      ),
    },

    {
      path: paths.benefitsConfig.root,
      element: (
        <AuthGuard>
          <DefaultLayout>
            <RoleBasedGuard permissions={benefitsConfig}>
              <BenefitsConfig />
            </RoleBasedGuard>
          </DefaultLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.benefitsConfig.root,
      element: (
        <AuthGuard>
          <DefaultLayout>
            <BenefitsConfig />
          </DefaultLayout>
        </AuthGuard>
      ),
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },

    ...errorRoutes,
  ]);
}
